import {
  normalizeTextLabelFontFamily,
  normalizeTextLabelFontSize,
  type CanvasTextLabelNodeProps,
} from "@/pages/canvas/types/canvasNodeData";
import { cn } from "@/utils";
import { BaseEntityCardQuickChat } from "@cohesive/ui-components";
import { LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  ORDERED_LIST,
  STRIKETHROUGH,
  UNORDERED_LIST,
} from "@lexical/markdown";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { NodeResizer, useReactFlow } from "@xyflow/react";
import { $getRoot, type EditorState, type LexicalEditor } from "lexical";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { LATTICE, snapToGrid } from "../canvas-controls.config";
import { useCanvasApi } from "../context/CanvasContext";
import { useCanvasFloatingChat } from "../context/CanvasFloatingChatContext";
import { useCanvasProcess } from "../context/CanvasProcessContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "../context/CanvasSelectionCountContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useSectionPreviewHighlight } from "../hooks/useSectionPreviewHighlight";
import { TextLabelFloatingToolbar } from "./TextLabelFloatingToolbar";
import { CanvasConnectHandles } from "./shared/CanvasConnectHandles";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";

const DEFAULT_WIDTH = 250;

const textNodeTheme = {
  paragraph: "mb-0",
  list: {
    ul: "list-disc list-inside ml-2",
    ol: "list-decimal list-inside ml-2",
    listitem: "ml-2",
  },
  text: {
    bold: "font-bold",
    strikethrough: "line-through",
    code: "bg-n-95/10 dark:bg-n-95/10 px-1 py-0.5 rounded text-xs font-mono",
  },
  link: "text-teal dark:text-teal underline hover:no-underline cursor-pointer",
};

const TRANSFORMERS = [
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  STRIKETHROUGH,
  UNORDERED_LIST,
  ORDERED_LIST,
];

function serializeToMarkdown(editorState: EditorState): string {
  let markdown = "";
  editorState.read(() => {
    markdown = $convertToMarkdownString(TRANSFORMERS);
  });
  // Lexical serializes an empty paragraph as "\n" — normalize to ""
  // so content is reliably empty when the editor has no visible text.
  return markdown.trim();
}

function isEditorEmpty(editorState: EditorState): boolean {
  let empty = true;
  editorState.read(() => {
    const root = $getRoot();
    const text = root.getTextContent().trim();
    empty = text === "";
  });
  return empty;
}

function EditorRefBridge({
  editorRef,
}: {
  editorRef: React.MutableRefObject<LexicalEditor | null>;
}) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editorRef.current = editor;
  }, [editor, editorRef]);
  return null;
}

function InitializerPlugin({
  value,
  isFocused,
  onInitialized,
}: {
  value: string;
  isFocused: boolean;
  onInitialized: () => void;
}) {
  const [editor] = useLexicalComposerContext();
  const lastValueRef = useRef<string>("");
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      lastValueRef.current = value;

      if (value) {
        editor.update(() => {
          $convertFromMarkdownString(value, TRANSFORMERS);
        });
      }

      onInitialized();
      return;
    }

    // While this user is actively typing, track the latest Loro value so our
    // own debounced saves don't create a stale diff that triggers a destructive
    // re-sync when the editor eventually loses focus.
    if (isFocused) {
      lastValueRef.current = value;
      return;
    }
    if (value === lastValueRef.current) return;

    lastValueRef.current = value;
    editor.update(() => {
      $convertFromMarkdownString(value || "", TRANSFORMERS);
    });
  }, [value, editor, isFocused, onInitialized]);

  return null;
}

export const TextLabelNode = memo(function TextLabelNode({
  id,
  data,
  selected,
  dragging,
  width: rawWidth,
  positionAbsoluteX,
  positionAbsoluteY,
}: CanvasTextLabelNodeProps) {
  // ReactFlow passes `undefined` explicitly when Loro has no width — JS
  // default params only apply for missing props, not explicit undefined.
  const width = rawWidth ?? DEFAULT_WIDTH;
  const canvas = useCanvasApi();
  const { setToolbarPopoverOpen } = useCanvasUi();
  const { canEdit } = canvas;
  const { dispatchProcess } = useCanvasProcess();
  const { openSession: openCanvasFloatingSession } = useCanvasFloatingChat();
  const { canvasId } = useParams<{ canvasId: string }>();
  const { setNodes } = useReactFlow();
  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      setIsPopoverOpen(open);
      setToolbarPopoverOpen(open);
    },
    [setToolbarPopoverOpen],
  );
  const nodeBodyRef = useRef<HTMLDivElement>(null);

  const content = data?.content || "";

  // isEditing: true  → overlay removed, ContentEditable is live, nodrag active
  // isEditing: false → overlay present, full area is draggable by React Flow
  const [isEditing, setIsEditing] = useState(selected && canEdit);
  // True only while the Lexical editor actually has browser focus — used by
  // InitializerPlugin to decide whether to sync incoming remote content.
  const [isFocused, setIsFocused] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showChatInput, setShowChatInput] = useState(false);

  const editorRef = useRef<LexicalEditor | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track selected in a ref so handleBlur (stable closure) can read the
  // latest value. This prevents deleting the node when the user clicks a
  // toolbar button — the node stays selected, so we skip deletion.
  const selectedRef = useRef(selected);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  // Sync Lexical editability whenever canEdit changes
  useEffect(() => {
    editorRef.current?.setEditable(canEdit);
  }, [canEdit]);

  // Auto-focus is only desired for nodes placed by THIS user.
  const shouldAutoFocusRef = useRef(selected && canEdit);
  // Snapshot of `selected` at the moment the overlay receives mousedown.
  // React Flow updates selection state during mousedown processing, so by
  // the time onClick fires `selected` is already true even for the very
  // first click. Reading it in mousedown lets us distinguish
  //   • "first click to select" (was false → don't enter edit mode)
  //   • "click on already-selected node" (was true → enter edit mode)
  const wasSelectedOnMouseDownRef = useRef(false);
  // Mouse-down screen coordinates used to distinguish click from drag when
  // deciding whether to enter edit mode.
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);
  // True while NodeResizer drag is in progress — gates the ResizeObserver
  // so it doesn't call setNodes mid-resize (which kills the visual handles).
  const isResizingRef = useRef(false);

  // Auto-focus after every render until focus is confirmed.
  // No deps — re-runs after each render cycle so we don't miss the frame
  // where the editor root element becomes available and focusable.
  // requestAnimationFrame defers the focus call past any competing click /
  // canvas-event processing in the same task, making it reliably win.
  useEffect(() => {
    if (!shouldAutoFocusRef.current) return;
    const rafId = requestAnimationFrame(() => {
      const rootEl = editorRef.current?.getRootElement();
      if (!rootEl) return;
      rootEl.focus({ preventScroll: true });
      // Once the element actually has focus, stop retrying.
      if (
        document.activeElement === rootEl ||
        rootEl.contains(document.activeElement)
      ) {
        shouldAutoFocusRef.current = false;
      }
    });
    return () => cancelAnimationFrame(rafId);
  }); // intentionally no dependency array

  // Keep RF's hit-test area in sync with actual content height.
  // NodeResizer sets an explicit height during resize, but this node uses
  // auto-height. Without this, the old height sticks after content shrinks
  // (e.g. text wraps to fewer lines after widening the node).
  //
  // Wrapped in `canvas.withoutUndo` (→ `room.history.disable`) because
  // auto-height is DERIVED state — the user didn't ask for it, the layout
  // engine emits it in response to text/width changes. Letting it land on
  // the undo stack would (a) push noise entries between real user actions
  // and (b) wipe the redo stack on every text re-flow, which made
  // Cmd+Shift+Z stop after one step.
  useEffect(() => {
    const el = nodeBodyRef.current;
    if (!el) return;
    let lastHeight = el.offsetHeight;
    const ro = new ResizeObserver(() => {
      if (isResizingRef.current) return;
      requestAnimationFrame(() => {
        if (!el.isConnected || isResizingRef.current) return;
        const measured = el.offsetHeight;
        if (measured === lastHeight) return;
        lastHeight = measured;
        canvas.withoutUndo(() =>
          setNodes((prev) =>
            prev.map((n) => (n.id === id ? { ...n, height: measured } : n)),
          ),
        );
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [id, setNodes, canvas]);

  const textColor = data?.text_color;
  const fontFamily = normalizeTextLabelFontFamily(data?.font_family);
  const fontSize = normalizeTextLabelFontSize(data?.font_size);
  const isBold = data?.is_bold ?? false;
  const isItalic = data?.is_italic ?? false;
  const isUnderline = data?.is_underline ?? false;

  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();

  // Multi-select unmounts this toolbar without Radix onOpenChange(false).
  // Clear chat + popover latches on multi-select or deselect — otherwise
  // showChatInput / isPopoverOpen alone would remount the wrong UI when
  // returning to single-select (node stays selected during multi-select).
  useEffect(() => {
    if (isMultiSelectToolbarOpen || !selected) {
      setShowChatInput(false);
      if (isPopoverOpen) {
        handlePopoverOpenChange(false);
      }
    }
  }, [
    isMultiSelectToolbarOpen,
    selected,
    isPopoverOpen,
    handlePopoverOpenChange,
  ]);

  const isSectionPreviewMember = useSectionPreviewHighlight(id);

  // Enter edit mode — only available for editors/owners.
  const handleStartEditing = useCallback(() => {
    if (!canEdit) return;
    setIsEditing(true);
    shouldAutoFocusRef.current = true;
  }, [canEdit]);

  // Debounced save — identical pattern to TextNoteNode.
  //
  // CRITICAL: Lexical's `OnChangePlugin` fires for any editor-state change
  // including selection-only ones (clicking into the editor, moving the
  // cursor, focus shifts). Without the `markdown === current` short-circuit
  // every cursor click would issue a Liveblocks write whose only meaningful
  // diff is `updated_at`, which bumps the undo stack and wipes the user's
  // redo history. Compare against the live `data?.content` (reactive prop
  // sourced from storage) so we never write a value that's already there.
  const handleContentChange = useCallback(
    (editorState: EditorState) => {
      if (!isInitialized) return;

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        const markdown = serializeToMarkdown(editorState);
        if (markdown === (data?.content ?? "")) return;
        canvas.updateNodeData(id, {
          content: markdown,
          updated_at: new Date().toISOString(),
        });
      }, 300);
    },
    [id, canvas, isInitialized, data?.content],
  );

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // On blur: exit edit mode and delete node if empty — but only when the node
  // is NOT still selected. If the user clicked a toolbar button, the node
  // stays selected (toolbar's onPointerDown stops propagation) so we skip
  // deletion, preventing the "toolbar click destroys empty node" bug.
  const handleBlur = useCallback(() => {
    setIsEditing(false);
    setIsFocused(false);
    if (!editorRef.current) return;
    if (
      canEdit &&
      !selectedRef.current &&
      isEditorEmpty(editorRef.current.getEditorState())
    ) {
      canvas.removeNode(id);
    }
  }, [id, canvas, canEdit]);

  const handleTextColorChange = useCallback(
    (color: string) => {
      canvas.updateNodeData(id, {
        text_color: color === "default" ? null : color,
      });
    },
    [id, canvas],
  );

  const handleFontFamilyChange = useCallback(
    (newFontFamily: string) => {
      canvas.updateNodeData(id, {
        font_family: normalizeTextLabelFontFamily(newFontFamily),
      });
    },
    [id, canvas],
  );

  const handleFontSizeChange = useCallback(
    (newFontSize: string) => {
      canvas.updateNodeData(id, {
        font_size: normalizeTextLabelFontSize(newFontSize),
      });
    },
    [id, canvas],
  );

  const handleBoldChange = useCallback(() => {
    canvas.updateNodeData(id, { is_bold: !isBold });
  }, [id, canvas, isBold]);

  const handleItalicChange = useCallback(() => {
    canvas.updateNodeData(id, { is_italic: !isItalic });
  }, [id, canvas, isItalic]);

  const handleUnderlineChange = useCallback(() => {
    canvas.updateNodeData(id, { is_underline: !isUnderline });
  }, [id, canvas, isUnderline]);

  const handleChatSubmit = useCallback(
    async (message: string) => {
      const content = data?.content || "";
      const flowSessionId = await dispatchProcess({
        nodeId: id,
        referencedEntities: [
          {
            entity_id: id,
            entity_type: "canvas_text_label",
            name: content.slice(0, 60) || "Text Label",
            platform: "cohesive",
          },
        ],
        action: "chat",
        customPrompt: message,
      });

      setShowChatInput(false);

      if (flowSessionId && canvasId) {
        openCanvasFloatingSession(flowSessionId);
      }
    },
    [id, data?.content, dispatchProcess, canvasId, openCanvasFloatingSession],
  );

  const initialConfig = useMemo(
    () => ({
      namespace: `TextLabelNode-${id}`,
      nodes: [ListNode, ListItemNode, LinkNode],
      onError: (error: Error) => {
        console.error("Lexical error in TextLabelNode:", error);
      },
      theme: textNodeTheme,
      // Set the correct initial editability so viewers never see an editable
      // editor on first paint. The useEffect below keeps it in sync on changes.
      editable: canEdit,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id], // canEdit intentionally excluded: LexicalComposer reads initialConfig
    // only on mount; subsequent changes are handled by setEditable in the effect.
  );

  // Toolbar follows selection only. Do not keep it mounted via isPopoverOpen /
  // isEditing after deselection — nested Format ▸ Color stacks used that latch
  // to survive canvas clicks. Popover open still notifies CanvasUi so RF can
  // ignore stray select:false while interacting with portaled menus.
  // Suppress when MultiSelectFloatingToolbar is mounted (multiSelectNodes >= 2).
  const showToolbar =
    canEdit &&
    !dragging &&
    !isMultiSelectToolbarOpen &&
    selected &&
    selectedNodeCount === 1;

  // Compute effective text color style
  const textColorStyle =
    textColor && textColor !== "default" ? textColor : undefined;

  return (
    <div
      ref={nodeBodyRef}
      className={cn(
        "relative",
        isSectionPreviewMember && "ring-2 ring-canvas-selection/60 rounded-lg",
      )}
      style={{ width }}
    >
      {/* Floating Toolbar — appears on select, no hover trigger */}
      {showToolbar && (
        <CanvasFloatingToolbarPortal
          positionAbsoluteX={positionAbsoluteX}
          positionAbsoluteY={positionAbsoluteY}
          width={width}
          topOffset={64}
        >
          {showChatInput ? (
            <BaseEntityCardQuickChat
              visible={true}
              onSubmit={handleChatSubmit}
              onClose={() => setShowChatInput(false)}
            />
          ) : (
            <TextLabelFloatingToolbar
              visible={true}
              textColor={textColor}
              fontFamily={fontFamily}
              fontSize={fontSize}
              isBold={isBold}
              isItalic={isItalic}
              isUnderline={isUnderline}
              onTextColorChange={handleTextColorChange}
              onFontFamilyChange={handleFontFamilyChange}
              onFontSizeChange={handleFontSizeChange}
              onBoldChange={handleBoldChange}
              onItalicChange={handleItalicChange}
              onUnderlineChange={handleUnderlineChange}
              onChatClick={() => setShowChatInput(true)}
              editor={editorRef.current}
              onPopoverOpenChange={handlePopoverOpenChange}
            />
          )}
        </CanvasFloatingToolbarPortal>
      )}

      {/* Resize handles — only in selected non-editing mode for editors/owners */}
      {selected && !isEditing && canEdit && (
        <NodeResizer
          minWidth={96}
          keepAspectRatio={false}
          onResizeStart={() => {
            isResizingRef.current = true;
          }}
          onResizeEnd={(_evt, params) => {
            isResizingRef.current = false;
            // Settle the WIDTH on the lattice (resize deltas are relative,
            // so legacy off-lattice widths keep their residue without
            // this). Height stays the content hug — exception X2.
            canvas.updateNodeDimensions(id, {
              width: Math.max(96, snapToGrid(params.width, LATTICE)),
            });
            // Sync final content height after resize completes. Same
            // rationale as the ResizeObserver block above: this is a
            // derived auto-height write, not a separate user action, so
            // it must not enter the undo stack on top of the resize
            // gesture that just landed.
            const el = nodeBodyRef.current;
            if (el) {
              const measured = el.offsetHeight;
              canvas.withoutUndo(() =>
                setNodes((prev) =>
                  prev.map((n) =>
                    n.id === id ? { ...n, height: measured } : n,
                  ),
                ),
              );
            }
          }}
        />
      )}

      <CanvasConnectHandles />

      {/* Node body — transparent background, height auto-expands with content */}
      <div
        className={cn(
          "relative rounded min-h-[32px]",
          isEditing && "ring-2 ring-canvas-selection/60",
        )}
      >
        {/*
          Interaction overlay — present whenever the node is NOT in edit mode.

          Cursor behaviour:
            • Not selected → cursor-grab  (whole area drags / selects)
            • Selected     → cursor-grab  (same cursor until edit mode is active)

          Click/drag state machine:
            • mousedown records whether the node was ALREADY selected at that
              instant (before React Flow processes the event).
            • click enters edit mode only when the node was already selected at
              mousedown. Browsers suppress click after drags, so drag-to-move
              still works naturally.
            • mouseup still checks movement threshold to swallow drag-release
              side-effects (pane deselect paths).

          This means:
            1st click on unselected  → select only (toolbar + handles appear)
            click while selected     → enter edit mode
            drag while selected      → move node (overlay has no nodrag)
        */}
        {!isEditing && (
          <div
            className={cn(
              "absolute inset-0 z-10 rounded",
              canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-default",
            )}
            onPointerDown={(e) => {
              wasSelectedOnMouseDownRef.current = selected ?? false;
              mouseDownPosRef.current = { x: e.clientX, y: e.clientY };

              // React Flow uses pointerdown (not mousedown) to initiate drag
              // and calls setPointerCapture, which redirects ALL subsequent
              // pointer/mouse events to RF's element. Stopping propagation at
              // the pointer-event level is the only way to prevent RF from
              // capturing the pointer and swallowing our pointerup.
              //
              // Only do this when already selected — unselected nodes need the
              // event to propagate so RF handles selection + drag.
              if (selected) {
                e.stopPropagation();
              }
            }}
            onPointerUp={(e) => {
              if (
                !wasSelectedOnMouseDownRef.current ||
                !mouseDownPosRef.current
              ) {
                mouseDownPosRef.current = null;
                return;
              }

              const dx = Math.abs(e.clientX - mouseDownPosRef.current.x);
              const dy = Math.abs(e.clientY - mouseDownPosRef.current.y);
              const moved = dx > 3 || dy > 3;
              mouseDownPosRef.current = null;

              if (moved) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }

              handleStartEditing();
            }}
          />
        )}

        <LexicalComposer initialConfig={initialConfig}>
          <EditorRefBridge editorRef={editorRef} />
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={cn(
                  "outline-none min-h-[32px]",
                  // nodrag/nowheel only while editing — lets Lexical handle
                  // text selection without React Flow intercepting the drag.
                  isEditing && "nodrag",
                  isEditing && "nowheel",
                )}
                onKeyDown={(e) => {
                  if (e.key === "Tab") e.preventDefault();
                  e.stopPropagation();
                  if (e.key === "Escape") {
                    // Focus the React Flow root instead of calling .blur()
                    // directly. If we just blur(), the browser forwards focus
                    // to the nearest other contenteditable in DOM order (e.g.
                    // a neighbouring TextNoteNode), triggering its onFocus and
                    // showing its toolbar. Focusing the RF root (tabIndex=0)
                    // absorbs the focus safely; the ContentEditable loses focus
                    // naturally, handleBlur fires, and nothing else activates.
                    const rfRoot = (
                      e.target as HTMLElement
                    ).closest<HTMLElement>(".react-flow");
                    if (rfRoot) {
                      rfRoot.focus({ preventScroll: true });
                    } else {
                      (e.target as HTMLElement).blur();
                    }
                  }
                }}
                onFocus={() => {
                  setIsEditing(true);
                  setIsFocused(true);
                }}
                onBlur={() => {
                  setIsFocused(false);
                  handleBlur();
                }}
                style={{
                  cursor: "text",
                  fontFamily,
                  fontSize,
                  fontWeight: isBold ? "bold" : "normal",
                  fontStyle: isItalic ? "italic" : "normal",
                  textDecoration: isUnderline ? "underline" : "none",
                  color: textColorStyle,
                  padding: "4px 6px",
                }}
              />
            }
            placeholder={
              canEdit && isEditing ? (
                <div
                  className="absolute top-0 left-0 pointer-events-none"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize,
                    color: "hsl(var(--foreground) / 0.55)",
                    padding: "4px 6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Add text
                </div>
              ) : null
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          <OnChangePlugin
            onChange={handleContentChange}
            ignoreSelectionChange={true}
          />
          <InitializerPlugin
            value={content}
            isFocused={isFocused}
            onInitialized={() => setIsInitialized(true)}
          />
        </LexicalComposer>
      </div>
    </div>
  );
});
